import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { setModalId, usePublicState } from '@/store/slices/publicSlice';
import {
  setUsersFilters,
  useUsersFilters,
} from '@/store/slices/reportFiltersSlice';
import {
  useGetCountriesQuery,
  useGetProfileTypesQuery,
} from '@/store/services/lookups';
import CustomModal from '@/components/atoms/CustomModal';
import InfoText from '@/components/atoms/InfoText';
import FormField from '@/components/FormField/FormField';
import Button from '@/components/atoms/Button';
import { Divider } from 'antd';
import DatesFilters from '../SharedComponents/DatesFilters';
import useDefaultReportDates from '../useDefaultReportDates';
import { useSharedFunctions } from '../SharedComponents/useSharedFunctions';

interface FormValues {
  profileTypes?: string[];
  location?: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

export default function FilterModal() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { modalId } = usePublicState();
  const filters = useUsersFilters();
  const { defaultFromDate, defaultToDate } = useDefaultReportDates();
  const { resetPage } = useSharedFunctions();

  const { data: countries, isLoading: isCountriesLoading } =
    useGetCountriesQuery();
  const { data: profileTypes, isLoading: profileTypesLoading } =
    useGetProfileTypesQuery();

  const schema = z
    .object({
      profileTypes: z.array(z.string()).optional(),
      location: z.string().optional(),
      fromDate: z.custom<Dayjs>().nullable(),
      toDate: z.custom<Dayjs>().nullable(),
    })
    .superRefine(({ fromDate, toDate }, ctx) => {
      if (fromDate && toDate && toDate.isBefore(fromDate, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t(
            'dispute.dateToBeforeFrom',
            'End date cannot be before start date'
          ),
          path: ['toDate'],
        });
      }
    });

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'all',
    values: {
      profileTypes: filters.profileTypes,
      location: filters.location || '',
      fromDate: filters.fromDate ? dayjs(filters.fromDate) : null,
      toDate: filters.toDate ? dayjs(filters.toDate) : null,
    },
  });

  const onClose = () => dispatch(setModalId(null));

  const watchAll = methods.watch();

  const appliedFiltersCount = [
    !!watchAll.profileTypes?.length,
    !!watchAll.location,
    watchAll.fromDate,
    watchAll.toDate,
  ].filter(Boolean).length;

  const onReset = () => {
    methods.reset({
      profileTypes: [],
      location: '',
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    });
  };

  const onSubmit = (values: FormValues) => {
    const fromDateStr = values.fromDate
      ? values.fromDate.format('YYYY-MM-DD')
      : '';
    const toDateStr = values.toDate ? values.toDate.format('YYYY-MM-DD') : '';

    dispatch(
      setUsersFilters({
        profileTypes: values.profileTypes || [],
        location: values.location || '',
        fromDate: fromDateStr,
        toDate: toDateStr,
      })
    );

    resetPage();
    onClose();
  };

  const countryList = Array.isArray(countries)
    ? countries
    : (countries as any)?.payload || [];

  const countryOptions = countryList.map((country: any) => ({
    label: country.name,
    value: country.code || country.id,
  }));

  return (
    <CustomModal
      title={t('home.filters', 'Filters')}
      isOpen={modalId === 'USERS_REPORT_MODAL'}
      onClose={onClose}
      subTitle={
        <div className="flex items-center gap-2">
          <InfoText
            size="sm"
            text={`${appliedFiltersCount} ${t('home.filterApplied', 'Filter applied')}`}
          />
          <button
            type="button"
            onClick={onReset}
            className="text-light-success hover:underline text-sm font-semibold"
          >
            {t('home.resetFilter', 'Reset filter')}
          </button>
        </div>
      }
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-4">
          {/* Location Section */}
          <FormField
            name="location"
            id="location"
            type="select"
            label={t('reports.location', 'Location')}
            placeholder={t('reports.selectLocation', 'Select location')}
            options={countryOptions}
            loading={isCountriesLoading}
            allowClear
            showSearch
          />

          <Divider className="bg-soft-background" />

          {/* Customer Type Section */}
          <FormField
            name="profileTypes"
            id="profileTypes"
            type="checkBox"
            label={t('reports.customerType', 'Customer type')}
            placeholder={t(
              'reports.selectCustomerType',
              'Select customer type'
            )}
            options={profileTypes?.map((el) => ({
              label: el.name,
              value: el.id,
            }))}
            className="flex justify-between"
            mode="multiple"
          />

          <Divider className="bg-soft-background" />

          {/* Date Range Section */}
          <DatesFilters />

          {/* Actions */}
          <div className="flex items-center justify-end gap-x-4 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[120px] h-[48px]"
            >
              {t('home.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="dark"
              className="min-w-[120px] h-[48px]"
            >
              {t('home.save', 'Save')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </CustomModal>
  );
}
